import { db } from "../lib/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { Certificate, CertificateTemplate } from "../types/certificate";

const generateCertificateNumber = (): string => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `EDUMI-${timestamp}-${random}`;
};

const generateVerificationCode = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

export const issueCertificate = async (
  userId: string,
  userName: string,
  courseId: string,
  courseTitle: string,
  instructorId: string,
  instructorName: string,
  completionDate: Date,
  options?: {
    grade?: string;
    score?: number;
    creditsEarned?: number;
    skills?: string[];
    templateId?: string;
  }
): Promise<string> => {
  const certificateNumber = generateCertificateNumber();
  const verificationCode = generateVerificationCode();

  const certificateData = {
    userId,
    userName,
    courseId,
    courseTitle,
    instructorId,
    instructorName,
    issueDate: Timestamp.fromDate(new Date()),
    completionDate: Timestamp.fromDate(completionDate),
    certificateNumber,
    verificationCode,
    grade: options?.grade || null,
    score: options?.score || null,
    creditsEarned: options?.creditsEarned || null,
    skills: options?.skills || [],
    templateId: options?.templateId || null,
    isPublic: true,
    createdAt: Timestamp.now(),
  };

  const docRef = await addDoc(collection(db, "certificates"), certificateData);
  
  // Update user's certificates array
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    const userData = userSnap.data();
    const certificates = userData.certificates || [];
    await updateDoc(userRef, {
      certificates: [...certificates, docRef.id],
    });
  }

  return docRef.id;
};

export const getCertificate = async (certificateId: string): Promise<Certificate | null> => {
  const certificateRef = doc(db, "certificates", certificateId);
  const certificateSnap = await getDoc(certificateRef);

  if (!certificateSnap.exists()) return null;

  const data = certificateSnap.data();
  return {
    id: certificateSnap.id,
    ...data,
    issueDate: data.issueDate.toDate(),
    completionDate: data.completionDate.toDate(),
    createdAt: data.createdAt.toDate(),
  } as Certificate;
};

export const getUserCertificates = async (userId: string): Promise<Certificate[]> => {
  const q = query(
    collection(db, "certificates"),
    where("userId", "==", userId),
    orderBy("issueDate", "desc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      issueDate: data.issueDate.toDate(),
      completionDate: data.completionDate.toDate(),
      createdAt: data.createdAt.toDate(),
    } as Certificate;
  });
};

export const getCourseCertificates = async (courseId: string): Promise<Certificate[]> => {
  const q = query(
    collection(db, "certificates"),
    where("courseId", "==", courseId),
    orderBy("issueDate", "desc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      issueDate: data.issueDate.toDate(),
      completionDate: data.completionDate.toDate(),
      createdAt: data.createdAt.toDate(),
    } as Certificate;
  });
};

export const verifyCertificate = async (
  verificationCode: string
): Promise<Certificate | null> => {
  const q = query(
    collection(db, "certificates"),
    where("verificationCode", "==", verificationCode)
  );

  const snapshot = await getDocs(q);
  
  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  const data = doc.data();
  
  return {
    id: doc.id,
    ...data,
    issueDate: data.issueDate.toDate(),
    completionDate: data.completionDate.toDate(),
    createdAt: data.createdAt.toDate(),
  } as Certificate;
};

export const verifyCertificateByNumber = async (
  certificateNumber: string
): Promise<Certificate | null> => {
  const q = query(
    collection(db, "certificates"),
    where("certificateNumber", "==", certificateNumber)
  );

  const snapshot = await getDocs(q);
  
  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  const data = doc.data();
  
  return {
    id: doc.id,
    ...data,
    issueDate: data.issueDate.toDate(),
    completionDate: data.completionDate.toDate(),
    createdAt: data.createdAt.toDate(),
  } as Certificate;
};

export const updateCertificateVisibility = async (
  certificateId: string,
  isPublic: boolean
): Promise<void> => {
  const certificateRef = doc(db, "certificates", certificateId);
  await updateDoc(certificateRef, { isPublic });
};

export const revokeCertificate = async (certificateId: string): Promise<void> => {
  const certificateRef = doc(db, "certificates", certificateId);
  await updateDoc(certificateRef, {
    isPublic: false,
    revokedAt: Timestamp.now(),
  });
};

export const createCertificateTemplate = async (
  instructorId: string,
  template: Omit<CertificateTemplate, "id" | "instructorId" | "createdAt" | "updatedAt">
): Promise<string> => {
  const templateData = {
    ...template,
    instructorId,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  const docRef = await addDoc(collection(db, "certificateTemplates"), templateData);
  return docRef.id;
};

export const getInstructorTemplates = async (
  instructorId: string
): Promise<CertificateTemplate[]> => {
  const q = query(
    collection(db, "certificateTemplates"),
    where("instructorId", "==", instructorId)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    } as CertificateTemplate;
  });
};

export const getCertificateStats = async (instructorId: string) => {
  const q = query(
    collection(db, "certificates"),
    where("instructorId", "==", instructorId)
  );

  const snapshot = await getDocs(q);
  const certificates = snapshot.docs.map((doc) => doc.data());

  const now = new Date();
  const thisMonth = certificates.filter((cert) => {
    const issueDate = cert.issueDate.toDate();
    return (
      issueDate.getMonth() === now.getMonth() &&
      issueDate.getFullYear() === now.getFullYear()
    );
  });

  const courseCounts: Record<string, { title: string; count: number }> = {};
  certificates.forEach((cert) => {
    if (!courseCounts[cert.courseId]) {
      courseCounts[cert.courseId] = { title: cert.courseTitle, count: 0 };
    }
    courseCounts[cert.courseId].count++;
  });

  const topCourses = Object.entries(courseCounts)
    .map(([courseId, data]) => ({
      courseId,
      courseTitle: data.title,
      count: data.count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return {
    totalIssued: certificates.length,
    issuedThisMonth: thisMonth.length,
    topCourses,
  };
};
